import { ROLES } from './auth/roles';

/** Job / professional roles for staff directory and intervention lead assignment. */
export const STAFF_ROLES = {
  SLT: 'Speech and Language Therapist',
  OT: 'Occupational Therapist',
  EP: 'Educational Psychologist',
  PHYSIO: 'Physiotherapist',
  EXTERNAL: 'External Specialist',
};

/** Ordered options for staff role dropdowns (school staff + external specialists). */
export const STAFF_ROLE_OPTIONS = [
  { value: ROLES.SENCO, label: 'SENCO / Senior Leader' },
  { value: ROLES.TEACHER, label: 'Teacher' },
  { value: ROLES.TA, label: 'Teaching Assistant' },
  { value: STAFF_ROLES.SLT, label: 'Speech and Language Therapist' },
  { value: STAFF_ROLES.OT, label: 'Occupational Therapist' },
  { value: STAFF_ROLES.EP, label: 'Educational Psychologist' },
  { value: STAFF_ROLES.PHYSIO, label: 'Physiotherapist' },
  { value: STAFF_ROLES.EXTERNAL, label: 'External Specialist' },
];

const LEGACY_ROLE_LABELS = {
  'Teaching Assistant': 'Teaching Assistant',
  Teacher: 'Teacher',
  SENCO: 'SENCO / Senior Leader',
};

/** Map legacy seed / free-text roles onto STAFF_ROLE_OPTIONS values for grouping. */
const ROLE_VALUE_ALIASES = {
  'Teaching Assistant': ROLES.TA,
  Teacher: ROLES.TEACHER,
  SENCO: ROLES.SENCO,
};

function resolveStaffRoleValue(role) {
  if (!role) return '';
  return ROLE_VALUE_ALIASES[role] || role;
}

/** Display label for a staff doc (seed data uses firstName/lastName; directory uses name). */
export function getStaffDisplayName(staff) {
  if (!staff) return '';
  const fromParts = [staff.firstName, staff.lastName].filter(Boolean).join(' ').trim();
  return (staff.name || fromParts || staff.email || 'Staff member').trim();
}

/** Human-readable label for a staff member's job role field. */
export function getStaffRoleLabel(role) {
  if (!role) return 'Staff';
  const match = STAFF_ROLE_OPTIONS.find((o) => o.value === role);
  if (match) return match.label;
  return LEGACY_ROLE_LABELS[role] || role;
}

/** Group active staff by role for optgroup dropdowns (intervention lead, etc.). */
export function groupStaffByRole(staffList = []) {
  const knownValues = new Set(STAFF_ROLE_OPTIONS.map((o) => o.value));
  const buckets = new Map(STAFF_ROLE_OPTIONS.map((o) => [o.value, []]));
  const other = [];

  for (const member of staffList) {
    const roleValue = resolveStaffRoleValue(member.role);
    if (knownValues.has(roleValue)) {
      buckets.get(roleValue).push(member);
    } else {
      other.push(member);
    }
  }

  const groups = STAFF_ROLE_OPTIONS
    .map((o) => ({
      value: o.value,
      label: o.label,
      staff: buckets.get(o.value) || [],
    }))
    .filter((g) => g.staff.length > 0);

  if (other.length > 0) {
    groups.push({ value: '_other', label: 'Other', staff: other });
  }

  return groups;
}

/** Flat list sorted by role tier then name (reliable in all browsers). */
export function sortStaffForPicker(staffList = []) {
  const roleOrder = STAFF_ROLE_OPTIONS.map((o) => o.value);
  return [...staffList].sort((a, b) => {
    const aIdx = roleOrder.indexOf(resolveStaffRoleValue(a.role));
    const bIdx = roleOrder.indexOf(resolveStaffRoleValue(b.role));
    const aOrder = aIdx === -1 ? roleOrder.length : aIdx;
    const bOrder = bIdx === -1 ? roleOrder.length : bIdx;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return getStaffDisplayName(a).localeCompare(getStaffDisplayName(b));
  });
}
